// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { EnterpriseProfileType } from "@mg/schemas/src/commons/user";
import { CircleNotch, UserPlus } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProjectInviteDialog } from "../../components/ask-puntt/ProjectInviteDialog";
import { AskPunttArtifactProvider } from "../../components/ask-puntt-review";
import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import {
  BREADCRUMB_HEADER_CHIP_CLASS,
  BREADCRUMB_TITLE_TEXT_CLASS,
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { buildMentionComposerMembers } from "../../utils/buildMentionComposerMembers";
import { useAppSelector } from "../../utils/hooks";
import { useMarkAskPunttArtifactSeen } from "../../utils/queries/askPuntt";
import {
  useAskPunttProject,
  useProjectReviews,
} from "../../utils/queries/askPunttProjects";
import { AskV2ConversationShell } from "../conversation/AskV2ConversationShell";
import { AskV2ProjectPrivateChats } from "../conversation/AskV2ProjectPrivateChats";
import { AskV2ProjectTabs } from "../conversation/AskV2ProjectTabs";
import { ASK_V2_PROJECTS_PATH } from "../paths";
import { AskV2OpenItems } from "../project/AskV2OpenItems";
import { openItemThreadQuestions } from "../project/openItemThreadQuestions";
import {
  projectThreadMessagesVersion,
  type UseProjectOpenItemsOptions,
  useProjectOpenItems,
} from "../project/useProjectOpenItems";
import { AskV2RailToggle } from "../rail/AskV2RailToggle";
import {
  ASK_V2_RAIL_WIDTH_CLASS,
  AskV2RightRail,
} from "../rail/AskV2RightRail";
import {
  collectRailContent,
  getReviewProgressMode,
  hasRailContent,
  hasReviewFindings,
  isReviewInPlaceForEmailNotification,
  isReviewItemFinished,
} from "../rail/collectRailContent";
import {
  ContextMemoryBadgeProvider,
  contextMemoryBadges,
} from "../rail/contextMemoryBadges";
import { mergeRailProgress } from "../rail/mergeRailProgress";
import { type AskV2RailSectionId } from "../rail/types";
import { useAskV2PlanProgress } from "../rail/useAskV2PlanProgress";
import { useAskV2RailReviewAutoOpen } from "../rail/useAskV2RailReviewAutoOpen";
import { AskV2ReviewWorkspace } from "../review/AskV2ReviewWorkspace";
import { reviewVersionOptions } from "../review/reviewVersionOptions";
import { useAskV2ReviewArtifactContext } from "../review/useAskV2ReviewArtifactContext";
import { useAskV2ReviewSidebarAutoCollapse } from "../review/useAskV2ReviewSidebarAutoCollapse";
import { useAskV2ReviewWorkspace } from "../review/useAskV2ReviewWorkspace";
import { AskV2ChatFileDrop } from "../transport/AskV2ChatFileDrop";
import { AskV2ThreadComposerStack } from "../transport/AskV2ThreadComposerStack";
import { AskV2SummonBar } from "../transport/SummonBar";
import { AskV2Thread } from "../transport/Thread";
import { AskV2ThreadLoading } from "../transport/ThreadLoading";
import { useAskV2Interactivity } from "../transport/useAskV2Interactivity";
import { useAskV2Transport } from "../transport/useAskV2Transport";

type AskV2ProjectPageProps = {
  projectId: string;
  reviewId?: string;
  commentId?: string;
  /** An open item to pick up in the composer on arrival (the home "For you" panel). */
  openItemId?: string;
  /** The review link asked to open its version comparison (`?compare=1`). */
  compareRequested?: boolean;
};

const DEFAULT_SECTIONS: Record<AskV2RailSectionId, boolean> = {
  progress: true,
  output: true,
  context: true,
};

export function AskV2ProjectPage({
  projectId,
  reviewId,
  commentId,
  openItemId,
  compareRequested,
}: AskV2ProjectPageProps) {
  const { t } = useTranslation("ui");
  const [tab, setTab] = useState<"feed" | "chats">("feed");
  const [railOpen, setRailOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState(DEFAULT_SECTIONS);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [openItemsSplit, setOpenItemsSplit] = useState(false);
  const [openItemsSplitHost, setOpenItemsSplitHost] =
    useState<HTMLDivElement | null>(null);
  const [openItemsRailHost, setOpenItemsRailHost] =
    useState<HTMLDivElement | null>(null);
  const setOpenItemsSplitNode = useCallback((node: HTMLDivElement | null) => {
    setOpenItemsSplitHost(node);
  }, []);
  const setOpenItemsRailNode = useCallback((node: HTMLDivElement | null) => {
    setOpenItemsRailHost(node);
  }, []);
  const closeOpenItemsSplit = useCallback(() => {
    setOpenItemsSplit(false);
  }, []);
  const workspace = useAskV2ReviewWorkspace({
    scope: { kind: "project", projectId },
    reviewId,
    commentId,
    compareRequested,
  });
  const projectQuery = useAskPunttProject(projectId);
  const reviewsQuery = useProjectReviews(projectId, {
    includeChatReviews: true,
  });
  const project = projectQuery.data?.project;
  const projectChats = projectQuery.data?.chats ?? [];
  const members = project?.members ?? [];
  const sharedChatId = project?.sharedChatId ?? null;
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const viewer = members.find((member) => member._id === currentUserId);
  const bindFeedTransport = tab === "feed" || workspace.isReviewOpen;
  const transport = useAskV2Transport({
    chatId: bindFeedTransport ? sharedChatId : null,
    enabled: bindFeedTransport && !!sharedChatId,
    presentation: "feed",
  });
  const projectOpenItemsOptions = useMemo((): UseProjectOpenItemsOptions => {
    if (!bindFeedTransport) return {};
    return {
      threadMessagesVersion: projectThreadMessagesVersion(
        transport.historyMessages,
      ),
    };
  }, [bindFeedTransport, transport.historyMessages]);
  const { query: projectOpenItemsQuery } = useProjectOpenItems(
    projectId,
    projectOpenItemsOptions,
  );
  const owner = members.find((member) => member._id === project?.ownerId);
  const ownerName = owner?.name ?? owner?.email ?? "Owner";
  // Roster edits are owner-OR-coordinator on the server
  // (`resolveMembershipAuthority` in christo's askPunttProject handler): the
  // project owner, or an enterprise COORDINATOR who can already reach the
  // project. Offering the invite control to anyone else offers an action the
  // server refuses with 403, so mirror the same pair here. `role` is the
  // active-enterprise profile on the session token; `ownerId` is compared as
  // well because the project's own `role` field is schema-optional (same
  // robust check the v1 project header uses, `routes/ask-puntt/campaign.tsx`).
  const viewerRole = useAppSelector((state) => state.auth.value?.role);
  const canManageMembers =
    (!!project &&
      (project.role === "owner" ||
        (!!currentUserId && project.ownerId === currentUserId))) ||
    viewerRole === EnterpriseProfileType.COORDINATOR;
  const isSharedChatOwner =
    !!currentUserId && transport.historyChat?.userId === currentUserId;
  // why: gate every feed write on the server's `canInteract` flag. An MG
  // cross-user reader otherwise reaches this page with a live composer whose
  // every write the server refuses — getChatHistory's cross-user bypass lets
  // them READ the feed, while the feed's binding to the project's shared chat
  // (a precondition, unchanged by this change) means christo answers their
  // POST /chats/{id}/messages (and abort/feedback) with the same 403 any
  // non-member gets. Fails OPEN exactly like AskV2ChatPage: the flag is absent
  // on older/cached payloads, and the lock may only engage on the server's
  // explicit say-so. See useAskV2Interactivity for the shared derivation.
  const { canInteract, gatedTransport } = useAskV2Interactivity(transport);
  const authorNameByUserId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const member of members) {
      const label = member.name?.trim() || member.email?.trim();
      if (member._id && label) map[member._id] = label;
    }
    return map;
  }, [members]);
  const mentionComposerMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        isSharedChatOwner ? members : [],
        transport.historyChat?.allowList ?? [],
      ),
    [isSharedChatOwner, members, transport.historyChat?.allowList],
  );
  // `## Behavior impact` — declared here, because this is a SECOND
  // user-visible change in a PR titled for the read-receipt gate, and the two
  // are unrelated: the shared project feed (and the review workspace's feed
  // thread) now resolve post authors from the ungated project roster instead
  // of the `isSharedChatOwner`-gated `mentionComposerMembers`. A member who
  // does not own the shared chat now sees real names and avatars on other
  // members' posts where they previously saw "Member" with a blank avatar.
  // This widens no data the client did not already hold — see the closing
  // paragraph — and the composer's @-mention roster stays gated.
  //
  // Attribution, NOT an invite surface — so the `isSharedChatOwner` gate above
  // must not be reused here. That gate exists to keep the composer's @-mention
  // autocomplete (and the directory search behind it) from handing the project
  // roster to someone who only holds the shared chat; `feedAttributionMembers`
  // (passed as the `feedMembers` prop) merely resolves the display name +
  // avatar of a post that is ALREADY on screen, and the @Name chips inside it
  // (AskPunttThread → `resolveFeedActorForUserMessage`, ChatMessage →
  // `resolveMentionActorsForFeedMessage`).
  //
  // Passing the gated list there labelled every OTHER member's post "Member"
  // with a blank avatar for everyone except the one person who owns the shared
  // chat: the project feed's chat is owned by whoever created the project, so
  // every other member is a non-owner, and getChatHistory hands a non-owner
  // `allowList: []` outright (handlers/catalyst/askPuntt.ts — "Shared users
  // see whether the chat is shared but not the full member list"). That left
  // the gated list EMPTY, on the one surface whose whole point is several
  // named people talking in the same thread. The viewer's own posts still
  // resolved, through the `feedActorName` fallback, which is what kept it
  // looking like a stale-name glitch rather than a dropped roster.
  //
  // The project roster is not privileged here: `useAskPunttProject` already
  // authorized this viewer for it, the header renders the same members as
  // avatars, and `authorNameByUserId` above is built from it ungated for the
  // review pane's comment authors — same job, same list.
  const feedAttributionMembers = useMemo(
    () =>
      buildMentionComposerMembers(
        members,
        transport.historyChat?.allowList ?? [],
      ),
    [members, transport.historyChat?.allowList],
  );
  // why: omitted canInteract fails open; only an explicit false is a read-only shared feed that must ignore drops
  // why-not: do not disable on the chats tab — private SummonBar registers the page-level drop target
  const fileDropDisabled = transport.historyChat?.canInteract === false;
  const progressMode = getReviewProgressMode({
    chat: transport.historyChat,
    messages: transport.historyMessages,
    reviews: reviewsQuery.data,
  });
  // why: getReviewProgressMode is running for any pending warhol turn; the email CTA waits until a review is kicked off
  const reviewInProgress = isReviewInPlaceForEmailNotification({
    chat: transport.historyChat,
    messages: transport.historyMessages,
    reviews: reviewsQuery.data,
  });
  const openItemNeededInRail = useMemo(() => {
    const needed = new Set<string>();
    for (const item of projectOpenItemsQuery.data?.openItems ?? []) {
      if (item.status === "cleared") continue;
      const text = item.needed?.trim();
      if (text) needed.add(text);
    }
    return needed;
  }, [projectOpenItemsQuery.data?.openItems]);

  const attachOpenItemToComposer = useCallback(
    (item: AskPunttOpenItem) => {
      gatedTransport.setOpenItemContext({
        id: item.id,
        kind: item.kind,
        needed: item.needed,
      });
      if (tab !== "feed") setTab("feed");
    },
    [gatedTransport, tab],
  );
  const threadQuestions = useMemo(
    () => openItemThreadQuestions(transport.historyMessages),
    [transport.historyMessages],
  );
  // why: the pane answers through the thread send — a second post would skip the open-item chip and the main chat
  const sendOpenItemAnswer = useCallback(
    (message: string) => {
      // why: the chip is only how this send names the item. Leaving it attached parks the context above an empty composer.
      void gatedTransport.send({ message, clearOpenItemContext: true });
    },
    [gatedTransport],
  );

  // why once per id: the home's "For you" row opens the project with one item
  // picked up; the list polls, and re-attaching on every poll would put the
  // chip back after the person removed it.
  const pickedUpOpenItemRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openItemId || pickedUpOpenItemRef.current === openItemId) return;
    const item = projectOpenItemsQuery.data?.openItems.find(
      (row) => row.id === openItemId && row.status !== "cleared",
    );
    if (!item) return;
    pickedUpOpenItemRef.current = openItemId;
    setOpenItemsSplit(true);
    attachOpenItemToComposer(item);
  }, [
    openItemId,
    projectOpenItemsQuery.data?.openItems,
    attachOpenItemToComposer,
  ]);

  const feedSummonBar = (
    <AskV2ThreadComposerStack
      chatId={sharedChatId}
      reviewInProgress={reviewInProgress}
    >
      <AskV2SummonBar
        transport={gatedTransport}
        requireBoundChatId
        mentionMembers={mentionComposerMembers}
        rosterMembers={isSharedChatOwner ? members : []}
        enableDirectoryMentions={isSharedChatOwner}
        composerAriaLabel={t("replyToProjectFeed")}
        readOnly={!canInteract}
        hasFindings={hasReviewFindings(reviewsQuery.data)}
      />
    </AskV2ThreadComposerStack>
  );
  const planProgress = useAskV2PlanProgress({
    messages: transport.historyMessages,
    mode: progressMode,
    // The transport's armed stream address is the one signal that holds
    // through the first seconds of a turn, when the thread can still be
    // showing the previous turn's answer while this turn's frame is in
    // flight. Without it the Progress section unmounts and remounts; see
    // `useAskV2PlanProgress`.
    turnInFlight: transport.streamMessageId != null,
    // Which turn that address is for; armed but absent from the thread means
    // the send is accepted and the payload has not returned it yet.
    turnInFlightUserId: transport.streamTurnUserId,
    // And this is the window BEFORE even that: the send has been taken locally
    // and the ACK that arms the address has not landed.
    turnPending: transport.sending,
  });
  const setRailOpenStable = useCallback(
    (open: boolean) => setRailOpen(open),
    [],
  );
  const setSectionOpenStable = useCallback(
    (
      updater: (
        current: Record<AskV2RailSectionId, boolean>,
      ) => Record<AskV2RailSectionId, boolean>,
    ) => setSectionOpen(updater),
    [],
  );
  useAskV2RailReviewAutoOpen({
    mode: progressMode,
    setRailOpen: setRailOpenStable,
    setSectionOpen: setSectionOpenStable,
  });
  const railContent = useMemo(() => {
    const base = collectRailContent({
      project,
      chat: transport.historyChat,
      messages: transport.historyMessages,
      reviews: reviewsQuery.data,
      // why-not: liveFindings — early findings render in the thread, not this rail
    });
    return mergeRailProgress(base, planProgress);
  }, [
    project,
    reviewsQuery.data,
    planProgress,
    transport.historyChat,
    transport.historyMessages,
  ]);
  const railHasContent = hasRailContent(railContent);
  const memoryBadges = useMemo(
    () =>
      contextMemoryBadges(
        railContent.context?.citedClaims,
        railContent.context?.memoryNumbers,
      ),
    [railContent],
  );
  const showRailToggle =
    !workspace.isReviewOpen && railHasContent && !openItemsSplit;
  // The switcher must offer every review the rail can OPEN, and "openable" is
  // `collectRailContent`'s `isReviewItemFinished` — which counts a row still
  // stamped `in_review` once AI comments have landed on it. christo's
  // `deriveTicketReviewStatus` holds `in_review` until the board flips
  // `isAIReviewed`, while `aiCommentCount` is counted live off the ticket, so
  // that pair is a normal window on every review, not a corrupt row.
  //
  // A strict `status !== "in_review"` dropped exactly those rows, so the
  // version the workspace was OPEN on was missing from its own picker: nothing
  // carried `aria-selected`, and with a single other finished review
  // `resolvedVersionOptions` was length 1, which `VersionPicker` reads as
  // `disabled` — she could not get back to the earlier version at all.
  //
  // A review that is genuinely still running (no comments yet) has nothing to
  // show and still stays out; `.trim().toLowerCase()` matches how every other
  // reader of this field normalizes it (`isReviewItemStillInFlight`).
  //
  // A review linked to earlier versions offers those versions alone (warhol
  // spec kevin.johnson/0007). why the fallback: an unlinked review is a
  // lineage of one, and the comparison's second picker reads this same list,
  // so narrowing it there would leave nothing to compare against.
  const versionOptions = useMemo(() => {
    const reviews = reviewsQuery.data?.reviews ?? [];
    const lineage = reviewVersionOptions(reviews, reviewId);
    if (lineage.length > 1) return lineage;
    return reviews.filter(isReviewItemFinished).map((item) => ({
      reviewId: item.id,
      title: item.title ?? item.name,
    }));
  }, [reviewsQuery.data?.reviews, reviewId]);
  const { mutate: markArtifactSeen } =
    useMarkAskPunttArtifactSeen(sharedChatId);
  const markReviewSeen = useCallback(
    (reviewIdToMark: string) => {
      markArtifactSeen({ kind: "review", reviewId: reviewIdToMark });
    },
    [markArtifactSeen],
  );
  const artifactContext = useAskV2ReviewArtifactContext({
    workspace,
    messages: transport.historyMessages,
    unseenArtifactKeys: transport.unseenArtifactKeys,
    markReviewSeen,
  });
  useAskV2ReviewSidebarAutoCollapse(workspace.isReviewOpen);

  if (projectQuery.isLoading) {
    return (
      <div
        className="ask-puntt-surface flex h-full items-center justify-center"
        data-testid="ask-v2-project-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <div
        className="ask-puntt-surface flex h-full flex-col items-center justify-center gap-4"
        data-testid="ask-v2-project-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          {t("campaign.couldNotLoadProject")}
        </Text>
        <a
          href={ASK_V2_PROJECTS_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="ask-v2-project-error-back"
        >
          {t("campaign.backToAllProjects")}
        </a>
      </div>
    );
  }

  const showProjectSidebar = !workspace.isReviewOpen && !openItemsSplit;

  return (
    <ContextMemoryBadgeProvider badges={memoryBadges}>
      <AskV2ChatFileDrop disabled={fileDropDisabled}>
        {/* why: this stays mounted when the sidebar unmounts. The list is
          portaled into the split; putting it inside the sidebar dropped the
          pane the moment the sidebar stopped rendering. */}
        <AskV2OpenItems
          projectId={projectId}
          members={members}
          reviewHasRun={progressMode === "completed"}
          projectOpenItemsOptions={projectOpenItemsOptions}
          currentUserId={currentUserId}
          viewerIsExpert={viewerRole === EnterpriseProfileType.EXPERT}
          projectOwnerId={project.ownerId}
          projectOwnerName={ownerName}
          onAttachOpenItem={attachOpenItemToComposer}
          onSendOpenItemAnswer={sendOpenItemAnswer}
          threadQuestions={threadQuestions}
          answersDisabled={
            transport.sending || transport.inFlight || !canInteract
          }
          railHost={showProjectSidebar ? openItemsRailHost : null}
          splitHost={
            openItemsSplit && !workspace.isReviewOpen
              ? openItemsSplitHost
              : null
          }
          onOpenSplit={() => setOpenItemsSplit(true)}
          onCloseSplit={closeOpenItemsSplit}
        />
        <AskV2ConversationShell
          testId="ask-v2-project-page"
          fullBleed={workspace.isReviewOpen}
          split={
            openItemsSplit && !workspace.isReviewOpen ? (
              <div
                ref={setOpenItemsSplitNode}
                data-testid="ask-v2-open-items-split"
                aria-label={t("askV2.openItems.listName")}
                className="flex h-full min-h-0 w-1/2 min-w-0 flex-col overflow-hidden border-l border-puntt-neutral-gray-5 bg-base-white"
              />
            ) : null
          }
          scrollResetKey={sharedChatId}
          scrollDeps={
            workspace.isReviewOpen || tab !== "feed" || !sharedChatId
              ? undefined
              : [transport.displayMessages]
          }
          autoScrollEnabled={!transport.inFlight}
          scrollSentSignal={transport.sending}
          onAddQuotedContext={
            workspace.isReviewOpen || tab === "chats"
              ? undefined
              : transport.addQuotedContext
          }
          header={
            <PageHeader className="w-full min-w-0 max-w-full gap-3 overflow-hidden border-puntt-neutral-gray-5">
              <BreadcrumbHeaderTrail className="min-w-0 flex-1 gap-6 overflow-hidden">
                <BreadcrumbHeaderRoot
                  to={ASK_V2_PROJECTS_PATH}
                  label={t("allProjects")}
                  testId="ask-v2-project-breadcrumb-all"
                />
                <span
                  className={`${BREADCRUMB_TITLE_TEXT_CLASS} truncate`}
                  data-testid="ask-v2-project-title"
                >
                  {project.name}
                </span>
                <span
                  className={`hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11 sm:inline ${BREADCRUMB_HEADER_CHIP_CLASS}`}
                  data-testid="ask-v2-project-owner"
                >
                  {t("ownedBy")} {ownerName}
                </span>
                <span className="sr-only" data-testid="ask-v2-project-id">
                  {projectId}
                </span>
                {!workspace.isReviewOpen ? (
                  <AskV2ProjectTabs
                    data-testid="ask-v2-project-tabs"
                    active={tab}
                    feedLabel={t("sharedProjectFeed")}
                    chatsLabel={t("privateChats")}
                    viewsLabel={t("projectViews")}
                    onChange={setTab}
                  />
                ) : null}
              </BreadcrumbHeaderTrail>
              <div className="ml-auto flex shrink-0 items-center gap-3">
                {members.length > 0 ? (
                  <div
                    className="flex shrink-0 items-center -space-x-2"
                    data-testid="ask-v2-project-members"
                  >
                    {members.slice(0, 5).map((member) => (
                      <AvatarWithInitials
                        key={member._id}
                        size={7}
                        name={member.name ?? member.email ?? "Member"}
                        avatar={member.avatar ?? undefined}
                        colorKey={member._id}
                        // why: christo hydrates every roster row's PERSISTED
                        // placeholder colors (`hydrateUserSummaries` →
                        // `avatarBackgroundColor` / `avatarTextColor`, which
                        // askPunttChatUserSummarySchema carries for exactly
                        // this: "so this user's chosen colors render in
                        // owner/member/share attribution"). Dropped here, this
                        // header fell back to `AvatarWithInitials`' own
                        // deterministic `colorKey` palette, so a member who
                        // picked their colors in Edit Profile rendered in one
                        // color on this page and in their chosen color on every
                        // OTHER surface that draws the same roster — the v1
                        // project header (routes/ask-puntt/campaign.tsx), the
                        // projects list card footer (ListCardGridFooter) and the
                        // private chat list (CampaignChatList) all pass the
                        // pair. Still `colorKey`-defaulted for legacy users who
                        // have no persisted pair.
                        backgroundColor={
                          member.avatarBackgroundColor ?? undefined
                        }
                        textColor={member.avatarTextColor ?? undefined}
                        className="ring-2 ring-puntt-neutral-gray-1"
                      />
                    ))}
                    {members.length > 5 ? (
                      <span
                        className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1"
                        data-testid="ask-v2-project-members-overflow"
                      >
                        +{members.length - 5}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {/* Gated on `canManageMembers` — owner or enterprise
                  coordinator, the same pair the christo handler now accepts.
                  It used to render for every viewer, which meant a plain
                  member saw a button whose POST the server refuses: they get
                  403 from `resolveMembershipAuthority`, and before that they
                  got a 404 from the owner-only lookup, so the refusal was
                  never something the UI could honour. */}
                {canManageMembers ? (
                  <button
                    type="button"
                    onClick={() => setInviteOpen(true)}
                    aria-label={t("inviteToProject")}
                    data-testid="ask-v2-project-invite"
                    className="flex shrink-0 items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9"
                  >
                    <UserPlus size={15} />
                    {t("invite")}
                  </button>
                ) : null}
                {showRailToggle ? (
                  <AskV2RailToggle
                    open={railOpen}
                    onToggle={() => setRailOpen((current) => !current)}
                  />
                ) : null}
              </div>
            </PageHeader>
          }
          composer={
            workspace.isReviewOpen || tab === "chats"
              ? undefined
              : feedSummonBar
          }
          rail={
            showProjectSidebar ? (
              <div
                className={`${ASK_V2_RAIL_WIDTH_CLASS} flex h-full w-[320px] shrink-0 flex-col overflow-hidden`}
                data-testid="ask-v2-project-sidebar"
              >
                <div className="ask-puntt-surface flex min-h-0 w-[320px] flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-3 [scrollbar-width:thin]">
                  <div ref={setOpenItemsRailNode} />
                  {showRailToggle ? (
                    <AskV2RightRail
                      embedded
                      open={railOpen}
                      content={railContent}
                      sectionOpen={sectionOpen}
                      onReviewOpen={workspace.openReview}
                      onSectionOpenChange={(id, open) =>
                        setSectionOpen((current) => ({
                          ...current,
                          [id]: open,
                        }))
                      }
                    />
                  ) : null}
                </div>
              </div>
            ) : null
          }
        >
          {workspace.isReviewOpen ? (
            <AskV2ReviewWorkspace
              workspace={workspace}
              transport={gatedTransport}
              versionOptions={versionOptions}
              feedPresentation="feed"
              feedActorName={viewer?.name ?? viewer?.email ?? undefined}
              feedActorAvatar={viewer?.avatar ?? undefined}
              feedMembers={feedAttributionMembers}
              currentUserId={currentUserId}
              authorNameByUserId={authorNameByUserId}
              artifactContext={artifactContext}
              composer={feedSummonBar}
              // The workspace mounts its own feed thread, whose per-row thumbs
              // POST `submitFeedback` outside AskV2TransportApi — the transport
              // gate above cannot reach them, so review mode needs the flag too.
              readOnly={!canInteract}
            />
          ) : (
            <AskPunttArtifactProvider value={artifactContext}>
              {tab === "chats" ? (
                <AskV2ProjectPrivateChats
                  projectId={projectId}
                  chats={projectChats}
                  sharedChatId={sharedChatId}
                  members={members}
                  currentUserId={currentUserId}
                />
              ) : !sharedChatId ? (
                <p
                  className="py-8 text-center text-sm text-puntt-neutral-gray-10"
                  data-testid="ask-v2-project-feed-empty"
                >
                  {t("campaignNewChatDraft.emptyLabel")}
                </p>
              ) : transport.historyLoading &&
                transport.messages.length === 0 ? (
                <AskV2ThreadLoading />
              ) : transport.historyError && transport.messages.length === 0 ? (
                <Text
                  as="p"
                  size="2"
                  color="gray"
                  data-testid="ask-v2-thread-error"
                  className="py-8 text-center"
                >
                  {t("askV2.pages.chat.loadError")}
                </Text>
              ) : (
                <AskV2Thread
                  transport={gatedTransport}
                  presentation="feed"
                  feedActorName={viewer?.name ?? viewer?.email ?? undefined}
                  feedActorAvatar={viewer?.avatar ?? undefined}
                  feedMembers={feedAttributionMembers}
                  currentUserId={currentUserId}
                  showEarlyFindings
                  suppressFindingDescriptions={openItemNeededInRail}
                  readOnly={!canInteract}
                />
              )}
            </AskPunttArtifactProvider>
          )}
        </AskV2ConversationShell>
        <ProjectInviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          projectId={projectId}
          projectName={project.name}
          members={members}
        />
      </AskV2ChatFileDrop>
    </ContextMemoryBadgeProvider>
  );
}
